import React from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./react-components/theme";

import { initSentry, Sentry } from "./shared/sentry";

import LoginForm from "./react-components/security/LoginForm";
import ForgotPasswordForm from "./react-components/security/ForgotPasswordForm";
import ForgotUsernameForm from "./react-components/security/ForgotUsernameForm";
import MultiFactorAuthForm from "./react-components/security/MultiFactorAuthForm";
import PasswordResetForm from "./react-components/security/PasswordResetForm";

// Error reporting (DSN from window._sentry; a no-op when unconfigured). The Bugsnag
// client this replaces pointed at a project that no longer exists.
initSentry();

// Same contract as the Bugsnag boundary it replaces: report, render nothing on a crash.
const ErrorBoundary = Sentry.ErrorBoundary;

// Read viewModel and flash from a <script type="application/json"> tag injected
// by the ColdFusion CFM template. This avoids HTML attribute encoding issues and
// keeps the data transfer mechanism simple and framework-agnostic.
function readViewData() {
    const dataEl = document.getElementById("login-view-data");
    if (dataEl) {
        const data = JSON.parse(dataEl.textContent);
        return {
            viewModel: data.viewModel || data.VIEWMODEL || {},
            flash: data.flash || data.FLASH || {},
        };
    }
    return { viewModel: {}, flash: {} };
}

function LoginApp() {
    const { viewModel, flash } = readViewData();

    // Check if this is a password reset page (rendered by passwordreset/show.cfm)
    const isPasswordReset = viewModel.firstName !== undefined;

    if (isPasswordReset) {
        return <PasswordResetForm viewModel={viewModel} flash={flash} />;
    }

    // Check if we-moved flag is set
    if (viewModel.agency && viewModel.agency.agWeveMoved === 1) {
        return (
            <div style={{ height: 400 }}>
                <div style={{ padding: 20, backgroundColor: "#2196F3", color: "#fff", margin: 25 }}>
                    This site is moving to a new server. If you are seeing this page, the navigation
                    process has not yet been completed. When the DSN has completely propagated, this
                    same URL can be used to navigate to the new location.
                </div>
            </div>
        );
    }

    return (
        <BrowserRouter>
            <Routes>
                <Route path="/login" element={<LoginForm viewModel={viewModel} flash={flash} />} />
                <Route path="/login/forgot-password" element={<ForgotPasswordForm viewModel={viewModel} />} />
                <Route path="/login/forgot-username" element={<ForgotUsernameForm viewModel={viewModel} />} />
                <Route path="/login/multi-factor" element={<MultiFactorAuthForm viewModel={viewModel} />} />
                <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
        </BrowserRouter>
    );
}

// Mount React app inside the content area (not on #app — that's the outer layout)
const mountEl = document.getElementById("react-root");
if (mountEl) {
    const root = createRoot(mountEl);
    root.render(
        <ErrorBoundary>
            <ThemeProvider theme={theme}>
                <LoginApp />
            </ThemeProvider>
        </ErrorBoundary>
    );
}
